// Report an accident or a near miss from the field (Session AOM).
//
// WHY IT EXISTS. An engineer who witnesses a near miss or an injury needs to get it down before it
// is forgotten, from the phone in their hand. A safety report that depends on somebody remembering
// to ring the office four hours later is a safety report that mostly does not happen.
//
// WRITE ONLY, AND THE ABSENCE OF A READ IS THE FEATURE. This screen posts and confirms. It does not
// list what has been reported, it does not show a record back, and it never shows the office's
// RIDDOR determination or investigation. An accident record carries an injured person's name,
// employer, contact details and injury detail, and none of that may reach a phone: the same rule
// engineer/history.ts and engineer/jobSheetDetail.ts already apply to defect triage internals and
// the RIDDOR flag. There is no engineer-side read call for this entity anywhere in src/lib/api.ts,
// and there must never be one.
//
// IT DOES NOT ASK FOR A RIDDOR CATEGORY, deliberately. That is an office judgement against the HSE
// criteria; asking an engineer to make a legal determination from a phone in a lift lobby, with no
// reference material and somebody possibly still hurt, would be the wrong question at the worst
// moment.
//
// ONLINE ONLY, AND THE DRAFT IS WHY THAT IS LIVEABLE. It is not queued through the offline outbox
// (see the handoff for the reasoning). What it does instead is keep what was typed in
// localStorage on every keystroke, so a failed send in a basement never loses the words: the
// engineer walks back into signal, reopens the screen and the text is still there.
import { presentableServerMessage } from "../../lib/serverMessage";
import { storageKeySuffix } from "../../lib/phonePreview";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { api, ApiError, type AccidentEventType, type EngineerSiteListItem } from "../../lib/api";

const OFFICE_PHONE = "0203 576 4760";
// Inside the Phone view the draft is kept under its own key (Session BDZ), so it never appears in the real app.
const DRAFT_KEY = `ele.engineer.accidentDraft${storageKeySuffix()}`;

// The engineer's words, in the order they would say them. "Near miss" leads because it is the one
// most likely to go unreported, and the whole point of putting this on the phone is to catch those.
const EVENT_OPTIONS: Array<{ value: AccidentEventType; label: string }> = [
  { value: "near_miss", label: "Near miss (nobody hurt)" },
  { value: "employee_injury", label: "Somebody who works for ELE was hurt" },
  { value: "non_worker_injury", label: "A member of the public was hurt" },
  { value: "dangerous_occurrence", label: "Something dangerous happened to the equipment" },
  { value: "occupational_disease", label: "A work-related illness" },
  { value: "other", label: "Something else" },
];

interface Draft {
  eventType: AccidentEventType;
  description: string;
  siteId: string;
  location: string;
  injuredName: string;
  dateOfEvent: string;
}

function emptyDraft(): Draft {
  return { eventType: "near_miss", description: "", siteId: "", location: "", injuredName: "", dateOfEvent: localNow() };
}

function readDraft(): Draft {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return emptyDraft();
    const parsed = JSON.parse(raw) as Partial<Draft>;
    // The stored time is deliberately NOT restored: a draft written yesterday should not silently
    // report today's incident with yesterday's timestamp. Everything else comes back.
    return { ...emptyDraft(), ...parsed, dateOfEvent: localNow() };
  } catch {
    return emptyDraft();
  }
}

export function ReportAccident() {
  const [params] = useSearchParams();
  const [form, setForm] = useState<Draft>(readDraft);
  const [sites, setSites] = useState<EngineerSiteListItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentRef, setSentRef] = useState<string | null>(null);

  // The site the engineer is at, when the screen was opened from a job.
  const presetSiteId = params.get("siteId") ?? "";

  useEffect(() => {
    api.engineer
      .sites()
      .then((s) => {
        setSites(s);
        // A preset site the list does not contain is CLEARED rather than left selected: a select
        // showing a blank while still holding a value is the trap that submits somebody else's site.
        setForm((f) => {
          const preset = presetSiteId && s.some((x) => x.id === presetSiteId) ? presetSiteId : f.siteId;
          const valid = preset && s.some((x) => x.id === preset) ? preset : "";
          return { ...f, siteId: valid };
        });
      })
      .catch(() => {
        // The site list is a convenience, not a requirement: a report with no site and a typed
        // location is perfectly valid, so a failed load must not block the form.
        setSites([]);
      });
  }, [presetSiteId]);

  // Keep the draft on every change, so a failed send never loses what was typed.
  useEffect(() => {
    if (sentRef) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    } catch {
      // A private window or full storage. The form still works; only the draft is lost.
    }
  }, [form, sentRef]);

  const set =
    (name: keyof Draft) =>
    (e: { target: { value: string } }) => {
      setForm((f) => ({ ...f, [name]: e.target.value }));
      setError(null);
    };

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!form.description.trim()) {
      setError("Say what happened, in your own words.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const ack = await api.engineer.reportAccident({
        eventType: form.eventType,
        description: form.description.trim(),
        dateOfEvent: form.dateOfEvent || undefined,
        siteId: form.siteId || undefined,
        location: form.location.trim() || null,
        injuredName: form.injuredName.trim() || null,
      });
      setSentRef(ack.ref);
      try {
        localStorage.removeItem(DRAFT_KEY);
      } catch {
        // Nothing to do: the report is already recorded, which is what matters.
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? presentableServerMessage(err.message) || "Could not send this. Check your signal and try again."
          : `Could not send this. You need a signal. What you typed has been kept on this phone, so try again when you have one, or ring the office on ${OFFICE_PHONE}.`,
      );
    } finally {
      setBusy(false);
    }
  }

  if (sentRef) {
    return (
      <div className="px-4 py-6">
        <div className="rounded-ele border border-ele-green bg-ele-card p-5 text-center">
          <h1 className="font-heading text-xl tracking-wide text-ele-strong">Thank you. The office has this.</h1>
          <p className="mt-2 text-sm text-ele-muted">
            Reference <span className="font-semibold text-ele-strong">{sentRef}</span>. Quote it if you ring in about this.
          </p>
          <p className="mt-2 text-xs text-ele-muted">
            The office will take it from here. If anybody is still hurt or the lift is unsafe, ring {OFFICE_PHONE} now as well.
          </p>
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <button
            onClick={() => {
              setSentRef(null);
              setForm(emptyDraft());
            }}
            className="flex min-h-11 items-center justify-center rounded-ele border border-ele-card-border font-menu text-xs tracking-wide text-ele-muted"
          >
            Report something else
          </button>
          <Link to="/engineer" className="flex min-h-11 items-center justify-center font-menu text-xs tracking-wider text-ele-green">
            Back to your jobs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-4">
      <Link to="/engineer" className="mb-3 inline-flex min-h-11 min-w-11 items-center font-menu text-xs tracking-wider text-ele-green">
        &larr; Back
      </Link>

      <div className="mb-4 flex items-start gap-2">
        <AlertTriangle aria-hidden size={20} className="mt-0.5 shrink-0 text-ele-danger" />
        <div>
          <h1 className="font-heading text-xl tracking-wide text-ele-strong">Report an accident or near miss</h1>
          <p className="mt-1 text-xs text-ele-muted">
            If anybody is hurt or in danger right now, deal with that first and ring {OFFICE_PHONE}. Use this to get it on record.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-muted">What kind of thing was it</span>
          <select
            aria-label="What kind of thing was it"
            value={form.eventType}
            onChange={set("eventType")}
            className="min-h-11 w-full rounded-ele border border-ele-card-border bg-ele-field px-3 py-2 text-base text-ele-strong outline-none focus:border-ele-green"
          >
            {EVENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-muted">What happened</span>
          <textarea
            aria-label="What happened"
            value={form.description}
            onChange={set("description")}
            rows={5}
            placeholder="In your own words. What you saw, what was being done at the time, what state things were left in."
            className="w-full rounded-ele border border-ele-card-border bg-ele-field px-3 py-2 text-base text-ele-strong outline-none focus:border-ele-green"
          />
        </label>

        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-muted">When</span>
          <input
            aria-label="When"
            type="datetime-local"
            value={form.dateOfEvent}
            onChange={set("dateOfEvent")}
            className="min-h-11 w-full rounded-ele border border-ele-card-border bg-ele-field px-3 py-2 text-base text-ele-strong outline-none focus:border-ele-green"
          />
        </label>

        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-muted">Which site</span>
          <select
            aria-label="Which site"
            value={form.siteId}
            onChange={set("siteId")}
            className="min-h-11 w-full rounded-ele border border-ele-card-border bg-ele-field px-3 py-2 text-base text-ele-strong outline-none focus:border-ele-green"
          >
            <option value="">Not one of my sites</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-muted">Where exactly</span>
          <input
            aria-label="Where exactly"
            value={form.location}
            onChange={set("location")}
            placeholder="e.g. motor room, ground floor lobby, the yard"
            className="min-h-11 w-full rounded-ele border border-ele-card-border bg-ele-field px-3 py-2 text-base text-ele-strong outline-none focus:border-ele-green"
          />
        </label>

        <label className="block">
          <span className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-muted">Who was hurt, if anybody</span>
          <input
            aria-label="Who was hurt, if anybody"
            value={form.injuredName}
            onChange={set("injuredName")}
            placeholder="Leave blank for a near miss"
            className="min-h-11 w-full rounded-ele border border-ele-card-border bg-ele-field px-3 py-2 text-base text-ele-strong outline-none focus:border-ele-green"
          />
        </label>

        {error && (
          <p role="alert" className="rounded-ele border border-ele-danger bg-red-500/10 px-3 py-2 text-sm text-ele-danger">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="flex min-h-12 w-full items-center justify-center rounded-ele bg-ele-green font-heading tracking-wide text-ele-bg disabled:opacity-50"
        >
          {busy ? "Sending..." : "Send it to the office"}
        </button>
        <p className="text-center text-xs text-ele-muted">
          This needs a signal. What you type is kept on this phone until it sends, so nothing is lost if it fails.
        </p>
      </form>
    </div>
  );
}

// "Now", as the value a datetime-local input wants, in the phone's own local time.
function localNow(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
